import React, { useState, useContext, useEffect } from "react";
import {
  useTheme,
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { Link } from "react-router-dom";
import { Dayjs } from "dayjs";
import { AuthContext } from "../../context/auth-context/AuthContext";
import useMemberDetails from "../../hooks/members/useMemberDetails";
import useCompanies from "../../hooks/companies/useCompanies";
import useMembers from "../../hooks/members/useMembers";
import FieldSection, { Field } from "../general/FieldSection";
import { CompanyDto, CreateContactPersonDto, CrmContactStatus } from "../../types/companyTypes";
import {
  isEmailInvalid as checkEmailInvalid,
  isPhoneInvalid as checkPhoneInvalid,
  isBirthdayInvalid as checkBirthdayInvalid,
} from "../../utils/validationUtils";
import { MembersFieldDto } from "../../types/membersTypes";

export type ContactPersonFormValues = {
  name: string;
  salutation: string;
  position: string;
  mobilePhone: string;
  email: string;
  birthday: Dayjs | null;
  yearUnknown: boolean;
  selectedCompany: CompanyDto | null;
  selectedJbtContact: MembersFieldDto | null;
  crmContactPreference: CrmContactStatus;
  crmContactStatusReason: string;
};

export const emptyContactPersonFormValues: ContactPersonFormValues = {
  name: "",
  salutation: "",
  position: "",
  mobilePhone: "",
  email: "",
  birthday: null,
  yearUnknown: false,
  selectedCompany: null,
  selectedJbtContact: null,
  crmContactPreference: CrmContactStatus.ACTIVE,
  crmContactStatusReason: "",
};

type Props = {
  title: string;
  submitLabel: string;
  cancelHref: string;
  dialogIntro: string;
  dialogActorLabel: string;
  initialValues?: ContactPersonFormValues;
  initialCompanyId?: number | null;
  showCrmContactFields?: boolean;
  onSubmit: (data: CreateContactPersonDto) => Promise<void> | void;
};

const SENTINEL_YEAR = 0;

const ContactPersonForm: React.FunctionComponent<Props> = ({
  title,
  submitLabel,
  cancelHref,
  dialogIntro,
  dialogActorLabel,
  initialValues,
  initialCompanyId,
  showCrmContactFields = false,
  onSubmit,
}) => {
  const theme = useTheme();
  const { auth } = useContext(AuthContext);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { memberDetails } = useMemberDetails(auth.userID!);
  const { allCompanies } = useCompanies();
  const { members } = useMembers();

  const buttonStyle = {
    marginTop: "20px",
    marginBottom: "20px",
    border: "0",
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": { color: "black" },
  };

  const [nameField, setNameField] = useState(initialValues?.name ?? "");
  const [salutationField, setSalutationField] = useState(initialValues?.salutation ?? "");
  const [positionField, setPositionField] = useState(initialValues?.position ?? "");
  const [mobilePhoneField, setMobilePhoneField] = useState(initialValues?.mobilePhone ?? "");
  const [emailField, setEmailField] = useState(initialValues?.email ?? "");
  const [birthdayField, setBirthdayField] = useState<Dayjs | null>(initialValues?.birthday ?? null);
  const [yearUnknown, setYearUnknown] = useState(initialValues?.yearUnknown ?? false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyDto | null>(initialValues?.selectedCompany ?? null);
  const [selectedJbtContact, setSelectedJbtContact] = useState<MembersFieldDto | null>(
    initialValues?.selectedJbtContact ?? null
  );
  const [crmContactPreferenceField, setCrmContactPreferenceField] = useState(
    initialValues?.crmContactPreference ?? CrmContactStatus.ACTIVE
  );
  const [crmContactStatusReasonField, setCrmContactStatusReasonField] = useState(
    initialValues?.crmContactStatusReason ?? ""
  );

  const [isNameFieldEmpty, setIsNameFieldEmpty] = useState(false);
  const [isSalutationFieldEmpty, setIsSalutationFieldEmpty] = useState(false);
  const [isJbtContactEmpty, setIsJbtContactEmpty] = useState(false);
  const [isCompanyEmpty, setIsCompanyEmpty] = useState(false);
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [isPhoneInvalid, setIsPhoneInvalid] = useState(false);
  const [isBirthdayInvalid, setIsBirthdayInvalid] = useState(false);
  const [isCrmContactStatusReasonEmpty, setIsCrmContactStatusReasonEmpty] = useState(false);
  const [isInitialized, setIsInitialized] = useState(!!initialValues);

  const [openDialog, setOpenDialog] = useState(false);

  // Rehydrate form once async initialValues (from details endpoint) arrive.
  useEffect(() => {
    if (!initialValues || isInitialized) return;
    setNameField(initialValues.name);
    setSalutationField(initialValues.salutation);
    setPositionField(initialValues.position);
    setMobilePhoneField(initialValues.mobilePhone);
    setEmailField(initialValues.email);
    setBirthdayField(initialValues.birthday);
    setYearUnknown(initialValues.yearUnknown);
    setSelectedCompany(initialValues.selectedCompany);
    setSelectedJbtContact(initialValues.selectedJbtContact);
    setCrmContactPreferenceField(initialValues.crmContactPreference);
    setCrmContactStatusReasonField(initialValues.crmContactStatusReason);
    setIsInitialized(true);
  }, [initialValues, isInitialized]);

  // Preselect company from a query-param-supplied id once companies have loaded.
  useEffect(() => {
    if (!initialCompanyId || selectedCompany || allCompanies.length === 0) return;
    const match = allCompanies.find((c) => c.companyId === initialCompanyId);
    if (match) setSelectedCompany(match);
  }, [initialCompanyId, allCompanies, selectedCompany]);

  const handleSubmit = () => {
    // Only the core identity/ownership fields are required; the remaining profile fields stay optional.
    let hasError = false;
    if (nameField.trim() === "") {
      setIsNameFieldEmpty(true);
      hasError = true;
    } else {
      setIsNameFieldEmpty(false);
    }
    if (salutationField.trim() === "") {
      setIsSalutationFieldEmpty(true);
      hasError = true;
    } else {
      setIsSalutationFieldEmpty(false);
    }
    if (selectedJbtContact === null) {
      setIsJbtContactEmpty(true);
      hasError = true;
    } else {
      setIsJbtContactEmpty(false);
    }
    if (selectedCompany === null) {
      setIsCompanyEmpty(true);
      hasError = true;
    } else {
      setIsCompanyEmpty(false);
    }
    const emailInvalid = emailField.trim() !== "" && checkEmailInvalid(emailField.trim());
    setIsEmailInvalid(emailInvalid);
    if (emailInvalid) hasError = true;
    const phoneInvalid = mobilePhoneField.trim() !== "" && checkPhoneInvalid(mobilePhoneField.trim());
    setIsPhoneInvalid(phoneInvalid);
    if (phoneInvalid) hasError = true;
    // Skip future-date check when year is unknown since we have no year to compare.
    const birthdayInvalid = birthdayField !== null && !yearUnknown && checkBirthdayInvalid(birthdayField);
    setIsBirthdayInvalid(birthdayInvalid);
    if (birthdayInvalid) hasError = true;
    if (
      showCrmContactFields &&
      crmContactPreferenceField === CrmContactStatus.NO_FURTHER_CONTACT_DESIRED &&
      crmContactStatusReasonField.trim() === ""
    ) {
      setIsCrmContactStatusReasonEmpty(true);
      hasError = true;
    } else {
      setIsCrmContactStatusReasonEmpty(false);
    }
    if (hasError) return;
    setOpenDialog(true);
  };

  const handleConfirm = async () => {
    if (!selectedCompany) return;
    let birthday: Date | null = null;
    if (birthdayField) {
      birthday = yearUnknown ? birthdayField.year(SENTINEL_YEAR).toDate() : birthdayField.toDate();
    }
    await onSubmit({
      companyId: selectedCompany.companyId,
      name: nameField,
      salutation: salutationField,
      position: positionField,
      mobilePhone: mobilePhoneField,
      email: emailField,
      birthday,
      jbtContactId: selectedJbtContact?.memberId ?? null,
      crmContactAllowed: crmContactPreferenceField !== CrmContactStatus.NO_FURTHER_CONTACT_DESIRED,
      crmContactStatus: crmContactPreferenceField,
      crmContactStatusReason:
        showCrmContactFields && crmContactPreferenceField !== CrmContactStatus.ACTIVE
          ? crmContactStatusReasonField.trim() || null
          : null,
    });
    setOpenDialog(false);
  };

  const fields: Field[] = [
    {
      label: "Name",
      state: nameField,
      width: "full",
      onChangeCallback: (e) => setNameField(e.target.value),
      type: "Text",
      helperText: isNameFieldEmpty ? "Bitte gib einen Namen ein!" : undefined,
      error: isNameFieldEmpty,
    },
    {
      label: "Anrede",
      state: salutationField,
      width: "full",
      onChangeCallback: (e) => setSalutationField(e.target.value),
      type: "Dropdown",
      values: [
        { value: "Herr", label: "Herr" },
        { value: "Frau", label: "Frau" },
        { value: "Divers", label: "Divers" },
      ],
      helperText: isSalutationFieldEmpty ? "Bitte wähle eine Anrede aus!" : undefined,
      error: isSalutationFieldEmpty,
    },
    {
      label: "Position",
      state: positionField,
      width: "full",
      onChangeCallback: (e) => setPositionField(e.target.value),
      type: "Text",
    },
    {
      label: "Telefon Mobil",
      state: mobilePhoneField,
      width: "full",
      onChangeCallback: (e) => setMobilePhoneField(e.target.value),
      type: "Text",
      helperText: "Format: +49 171 1234567",
      error: isPhoneInvalid,
    },
    {
      label: "E-Mail",
      state: emailField,
      width: "full",
      onChangeCallback: (e) => setEmailField(e.target.value),
      type: "Text",
      helperText: "Format: name@domain.de",
      error: isEmailInvalid,
    },
    {
      label: "Geburtsdatum",
      state: birthdayField,
      width: "full",
      onChangeCallback: (value: Dayjs | null) => setBirthdayField(value),
      type: "Date",
      helperText: isBirthdayInvalid ? "Das Geburtsdatum darf nicht in der Zukunft liegen." : undefined,
      error: isBirthdayInvalid,
      views: yearUnknown ? ["month", "day"] : ["year", "month", "day"],
      format: yearUnknown ? "DD.MM." : "DD.MM.YYYY",
    },
    {
      label: "Geburtsjahr unbekannt",
      state: yearUnknown,
      width: "full",
      onChangeCallback: (e) => {
        setYearUnknown(e.target.checked);
        setIsBirthdayInvalid(false);
      },
      type: "Checkbox",
    },
  ];

  if (showCrmContactFields) {
    fields.push(
      {
        label: "Kontaktpräferenz",
        state: crmContactPreferenceField,
        width: "full",
        onChangeCallback: (event) => {
          const nextValue = event.target.value as CrmContactStatus;
          setCrmContactPreferenceField(nextValue);
          if (nextValue === CrmContactStatus.ACTIVE) {
            setCrmContactStatusReasonField("");
            setIsCrmContactStatusReasonEmpty(false);
          }
        },
        type: "Dropdown",
        values: [
          { value: CrmContactStatus.ACTIVE, label: "Kontakt erwünscht" },
          { value: CrmContactStatus.POTENTIAL_FUTURE_PROJECT, label: "Potenzielles Folgeprojekt" },
          { value: CrmContactStatus.NO_FURTHER_CONTACT_DESIRED, label: "Kein weiterer Kontakt gewünscht" },
        ],
      },
      {
        label: "Begründung Kontaktpräferenz",
        state: crmContactStatusReasonField,
        width: "full",
        onChangeCallback: (e) => setCrmContactStatusReasonField(e.target.value),
        type: "TextBig",
        rows: 3,
        helperText:
          crmContactPreferenceField === CrmContactStatus.NO_FURTHER_CONTACT_DESIRED && isCrmContactStatusReasonEmpty
            ? "Bitte gib eine Begründung an."
            : undefined,
        error:
          crmContactPreferenceField === CrmContactStatus.NO_FURTHER_CONTACT_DESIRED && isCrmContactStatusReasonEmpty,
        disabled: crmContactPreferenceField === CrmContactStatus.ACTIVE,
      }
    );
  }

  const selectableMembers: MembersFieldDto[] = members.map((m) => ({
    memberId: m.memberId,
    firstname: m.firstname,
    lastname: m.lastname,
  }));

  const jbtContactField: Field = {
    label: "JBT-Kontakt",
    state: selectedJbtContact,
    width: "full",
    onChangeCallback: (_event: React.SyntheticEvent, value: MembersFieldDto[] | MembersFieldDto | null) => {
      setSelectedJbtContact(value as MembersFieldDto | null);
      setIsJbtContactEmpty(value === null);
    },
    type: "MemberAutocomplete",
    value: selectedJbtContact,
    options: selectableMembers,
    helperText: isJbtContactEmpty ? "Bitte wähle einen JBT-Kontakt aus." : undefined,
    error: isJbtContactEmpty,
  };

  const companyNames = allCompanies.map((c) => c.name);

  const companyField: Field = {
    label: "Unternehmen",
    state: selectedCompany?.name || null,
    width: "full",
    onChangeCallback: (_event: React.ChangeEvent<object>, value: unknown) => {
      const name = value as string | null;
      const company = allCompanies.find((c) => c.name === name) || null;
      setSelectedCompany(company);
      setIsCompanyEmpty(company === null);
    },
    type: "Autocomplete",
    value: selectedCompany?.name || null,
    options: companyNames,
    helperText: isCompanyEmpty ? "Bitte wähle ein Unternehmen aus." : undefined,
    error: isCompanyEmpty,
  };

  const birthdaySummary = birthdayField
    ? yearUnknown
      ? birthdayField.format("DD.MM")
      : birthdayField.format("DD.MM.YYYY")
    : "";

  // Reuse the same summary rows for both create and edit confirmation dialogs.
  const summaryRows: Array<[string, string]> = [
    [dialogActorLabel, `${memberDetails?.firstname ?? ""} ${memberDetails?.lastname ?? ""}`.trim()],
    ["Name", nameField],
    ["Anrede", salutationField],
    ["Position", positionField],
    ["Telefon Mobil", mobilePhoneField],
    ["E-Mail", emailField],
    ["Geburtsdatum", birthdaySummary],
    ["JBT-Kontakt", selectedJbtContact ? `${selectedJbtContact.firstname} ${selectedJbtContact.lastname}` : ""],
    ["Unternehmen", selectedCompany?.name ?? ""],
  ];

  if (showCrmContactFields) {
    summaryRows.push(
      [
        "Kontaktpräferenz",
        crmContactPreferenceField === CrmContactStatus.NO_FURTHER_CONTACT_DESIRED
          ? "Kein weiterer Kontakt gewünscht"
          : crmContactPreferenceField === CrmContactStatus.POTENTIAL_FUTURE_PROJECT
          ? "Potenzielles Folgeprojekt"
          : "Kontakt erwünscht",
      ],
      [
        "Begründung Kontaktpräferenz",
        crmContactPreferenceField !== CrmContactStatus.ACTIVE ? crmContactStatusReasonField : "",
      ]
    );
  }

  return (
    <div>
      <Grid container spacing={1} sx={{ paddingTop: "30px", paddingLeft: "10px" }}>
        <Grid item xs={12}>
          <FieldSection title={title} fields={[...fields, jbtContactField, companyField]} />
        </Grid>
        <Grid item>
          <Button sx={buttonStyle} variant="contained" onClick={handleSubmit}>
            {submitLabel}
          </Button>
        </Grid>
        <Grid item>
          <Button sx={buttonStyle} variant="contained" component={Link} to={cancelHref}>
            Abbrechen
          </Button>
        </Grid>
      </Grid>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Bestätigung</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {dialogIntro}
            {summaryRows.map(([label, value]) => (
              <div key={label}>
                {label}: {value}
              </div>
            ))}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Abbrechen
          </Button>
          <Button onClick={handleConfirm} color="primary">
            Bestätigen
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ContactPersonForm;
